import React, {useMemo} from 'react';
import {FormControl, InputLabel, MenuItem, Select, Typography} from "@mui/material";
import {SelectChangeEvent} from "@mui/material";

type Props = {
  id?: string,
  label?: string,
  name?: string,
  onChange?: (event: SelectChangeEvent) => void,
  options?: { value: string | null | undefined, label: string }[],
  placeholder?: string,
  required?: boolean,
  displayEmpty?: boolean,
  value?: string | any,
};

/**
 * A simple select component
 * @param id the id of the select
 * @param label the text to display above the select
 * @param name the name of the select element
 * @param required whether the select is required
 * @param value the currently selected value
 * @param onChange the function to call when the value changes
 * @param options the list of options to display in the select
 * @param placeholder the placeholder text to display when no value is selected. Will display only if selectProps.displayEmpty is true
 * @param displayEmpty whether to display the placeholder text when no value is selected
 * @constructor
 */
const SimpleSelect = ({
                        id = "select",
                        label = "Select",
                        name = "",
                        onChange = () => {},
                        options = [],
                        placeholder = "Select a value",
                        displayEmpty = false,
                        required = false,
                        value = "",
                      }: Props) => {
  const memoizedOptions = useMemo(() => {
    return options.map(item => <MenuItem value={item.value} key={item.value}>{item.label}</MenuItem>)
  }, [options]);

  const renderValue: (value: any) => any = (value) => {
    if (!displayEmpty) return value;
    if (value === null || value === undefined) return <Typography>{placeholder}</Typography>;
    return <Typography>{value}</Typography>;
  }

  return (
    <FormControl variant='standard' required={required} fullWidth>
      <InputLabel htmlFor={id}>{label}</InputLabel>
      <Select
        id={id}
        onChange={onChange}
        value={value}
        variant={'standard'}
        name={name}
        renderValue={renderValue}
        displayEmpty={displayEmpty}
      >
        {memoizedOptions}
      </Select>
    </FormControl>
  );
};

export default SimpleSelect;